@import './fluid-tabs.theme.less';

@import '../../styles/mixins/fluid-transitions.mixin.less';

// ================================================================ //
// -- Theme Definition

:host {
  display: block;
  position: relative;

  *, ::after, ::before {
    box-sizing: border-box;
  }

  .fluid-tabs {

    /**
     * LEGACY THEME
     */
    &.legacy {
      @import '../../styles/variables-iuw-legacy.less';
      @tabs-tokens: @tabs-tokens-legacy;
      @tabs();
    }

    /**
     * LIBERTY MUTUAL THEME
     */
    &.lm {
      @import '../../styles/variables-lm.less';
      @tabs-tokens: @tabs-tokens-lm;
      @tabs();
    }

    /**
     * LM CORPORATE THEME
     */
    &.corp, &.default {
      @import '../../styles/variables-lm-corp.less';
      @tabs-tokens: @tabs-tokens-corp;
      @tabs();
    }

    /**
     * PORTAL THEME
     */
    &.portal {
      @import '../../styles/variables-lm-portal.less';
      @tabs-tokens: @tabs-tokens-portal;
      @tabs();
    }
  }
}


// ================================================================ //
// -- Component Detached Ruleset

@tabs: {
  @content: @tabs-tokens[@tabs-content];
  @tab-btn: @tabs-tokens[@tab-button];
  @tab-btn-horizontal: @tab-btn[@horizontal];
  @tab-btn-vertical: @tab-btn[@vertical];
  @tab-btn-active: @tab-btn[@active];
  @ruleset--tab-btn-hover: @tab-btn[@ruleset--hover];

  // ==== Local Variables

  @add-tab-column-gap: 1em;

  // ---------------- //

  font-family: @font-family;
  font-size: @tabs-tokens[font-size];
  font-weight: @tabs-tokens[font-weight];
  line-height: @tabs-tokens[line-height];
  color: @tabs-tokens[color];
  margin-top: @tabs-tokens[margin-top];
  display: flex;

  // ==== Vertical Tabs

  &.vertical {
    flex-direction: row;
  }

  // ==== Horizontal Tabs

  &.horizontal {
    flex-direction: column;
  }

  // ==== Tabs Container

  .tabs-container {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    column-gap: @add-tab-column-gap;
    position: relative;

    // == "Border" Between Tab Buttons & Tab Content

    &::before {
      display: block;
      position: absolute;
      background-color: @content[border-color];
      content: "";
    }

    // == Horizontal Tabs

    &.horizontal {
      flex-direction: row;

      &::before {
        height: @content[border-width];
        top: calc(100% - @content[border-width]);
        left: 0;
        right: 0;
      }
    }

    // == Vertical Tabs

    &.vertical {
      flex-direction: column;

      &::before {
        width: @content[border-width];
        left: calc(100% - @content[border-width]);
        top: 0;
        bottom: 0;
      }
    }
  }

  // ==== Tabs List Container

  .tabs-list-container {
    flex: 1 1 auto;
    min-width: 0; // Fix flexbox overflow bug
    position: relative;

    &.vertical-layout {
      width: 100%;
    }

    &.has-add-tab-btn {
      flex: 0 1 auto; // Add button after tab list

      .tabs-list.nav-arrows {
        padding-right: 0; // Remove extra padding when "Add Tab" Button visible
      }

      .tabs-nav-container {
        bottom: @content[border-width];
      }
    }
  }

  // ==== Tabs List

  .tabs-list {
    display: flex;
    position: relative;
    white-space: nowrap;
    overflow-x: auto;
    scroll-behavior: smooth;
    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    &.nav-arrows {
      padding-right: 3em; // Width of FluidTabNavigation arrow
    }

    &.vertical-layout {
      flex-direction: column;
    }
  }

  // ==== Tab Buttons

  .fluid-tab-btn {
    @button-reset();
    flex: @tab-btn[flex];
    display: inline-flex;
    justify-content: center;
    align-items: center;
    gap: 0.5em;
    position: relative;
    font-family: inherit;
    font-size: @tab-btn[font-size];
    font-weight: inherit;
    line-height: inherit;
    color: @tab-btn[color];
    background-color: @tab-btn[background-color];
    padding-top: @tab-btn[padding-top];
    padding-bottom: @tab-btn[padding-bottom];
    padding-left: @tab-btn[padding-left];
    padding-right: @tab-btn[padding-right];
    border-color: @tab-btn[border-color];
    border-style: @border-style;
    cursor: pointer;
    max-height: 3em;

    @disable-user-select();
    .transition(@property: background-color, border-color, color; @duration: short);

    @media @screen-ie-only {
      flex: 0 0 auto;
    }

    // == Horizontal Layout

    &.horizontal {
      border-width: @tab-btn-horizontal[border-width];
      border-radius: @tab-btn-horizontal[border-radius];
      margin-right: @tab-btn-horizontal[margin];

      &.active {
        border-bottom-width: @tab-btn-active[border-width];
        border-bottom-style: solid;
        border-bottom-color: @tab-btn-active[border-color];
      }
    }

    // == Vertical Layout

    &.vertical {
      justify-content: flex-end;
      border-width: @tab-btn-vertical[border-width];
      border-radius: @tab-btn-vertical[border-radius];
      margin-bottom: @tab-btn-vertical[margin];

      &.active {
        border-right-width: @tab-btn-active[border-width];
        border-right-style: solid;
        border-right-color: @tab-btn-active[border-color];
      }
    }

    // == Selected

    &.active {
      background-color: @tab-btn-active[background-color];
      color: @tab-btn-active[color];
      font-weight: @tab-btn-active[font-weight];
    }

    // == Disabled

    &.disabled {
      cursor: not-allowed;

      .tab-btn-label,
      [class^="tab-icon-"] {
        opacity: 0.6;
      }
    }

    // == Context Content

    &.has-context-content {

      .tab-context-content {
        display: flex;
        justify-content: center;
        align-items: center;

        @media @screen-lg {
          &.show-on-hover {
            display: none;
          }
        }
      }

      &:hover {
        .tab-context-content {
          &.show-on-hover {
            display: flex;
          }
        }
      }
    }

    // == Hover & Focus States

    &:not(.disabled) {

      &:hover:not(.active),
      &:focus-visible {
        @ruleset--tab-btn-hover();
      }

      &:focus,
      &:focus-visible {
        outline: 0;
      }
    }
  }

  // ==== Tab Button Icons

  .tab-icon-before,
  .tab-icon-after {
    display: flex;
    justify-content: center;
    align-items: center;
    color: inherit; // Sets FLUID Icon "fill" color
    fill: currentColor; // Sets non-FLUID icon "fill" color
    white-space: normal;

    &.slotted {
      svg {
        width: 1em;
        height: 1em;
      }
    }
  }

  // ==== "Add Tab" Button

  .add-tab-container {
    flex: 0 1 auto;
    align-self: stretch;
    display: flex;
    justify-content: center;
    align-items: center;
    padding-left: @tab-btn[padding-left] / 2;
    border-width: 0 0 0 @content[border-width];
    border-color: @content[border-color];
    border-style: solid;

    &.vertical-layout {
      width: 100%;
      padding-top: @tab-btn[padding-top];
      padding-bottom: @tab-btn[padding-bottom];
      padding-right: @tab-btn[padding-right] / 2;
      border-width: @content[border-width] 0 0 0;
    }
  }

  .add-tab-btn {
    white-space: nowrap;
  }

  // ==== Tab Content

  .tabs-content {
    position: relative;
    width: 100%;

    // == Horizontal Tab Content

    &.horizontal {
      padding-top: @content[padding];
      padding-bottom: @content[padding];
    }

    // == Vertical Tab Content

    &.vertical {
      padding-left: @content[padding];
      padding-right: @content[padding];
    }
  }
}


// ================================================================ //
// -- Utility Detached Ruleset(s)

@button-reset: {
  background-color: transparent;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  margin: 0;
  padding: 0;
  border: none;
  cursor: pointer;
}

@disable-user-select: {
  -webkit-touch-callout: none; // iOS Safari
  -webkit-user-select: none; // Safari
  -khtml-user-select: none; // Konqueror HTML
  -moz-user-select: none; // Old versions of Firefox
  -ms-user-select: none; // Internet Explorer/Edge
  user-select: none;
}
